import { Search, MousePointer2, Compass, Sparkles } from "lucide-react";

const features = [
  {
    icon: Search,
    name: "Search naturally",
    summary: "Ask in plain language. Equal searches across the page — and beyond it.",
    example: "“Show me flights to Lisbon under £200 next month.”",
  },
  {
    icon: MousePointer2,
    name: "Select intelligently",
    summary: "Pick the right item by meaning, not pixel. No precision required.",
    example: "“Select the black hoodie in medium.”",
  },
  {
    icon: Compass,
    name: "Navigate instantly",
    summary: "Jump between sections, pages, and flows without hunting menus.",
    example: "“Take me to my saved items, then to checkout.”",
  },
  {
    icon: Sparkles,
    name: "Act without friction",
    summary: "Complete tasks end-to-end — add, submit, confirm — in a single step.",
    example: "“Add to cart and apply my student discount.”",
  },
];

const Solution = () => {
  return (
    <section
      id="solution"
      data-testid="solution-section"
      className="relative py-28 md:py-36 border-t border-white/[0.06] bg-[#070707]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 shadow-[0_0_10px_rgba(99,102,241,0.9)]" />
            <span className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-zinc-400">
              The product · EqualLayer
            </span>
          </div>
          <h2 className="mt-6 font-display font-semibold tracking-tight text-white text-3xl md:text-4xl lg:text-5xl text-balance">
            Type what you want.{" "}
            <span className="bg-gradient-to-r from-indigo-300 via-violet-300 to-sky-300 bg-clip-text text-transparent">
              Equal handles the rest.
            </span>
          </h2>
          <p className="mt-6 font-body text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
            EqualLayer sits on top of any website and turns intent into action. Instead of learning every
            interface, users simply say what they need — and EqualLayer searches, selects, navigates, and acts.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={i}
                data-testid={`feature-card-${f.name.split(" ")[0].toLowerCase()}`}
                className="group relative rounded-2xl border border-white/[0.08] bg-white/[0.015] p-6 transition-all hover:-translate-y-1 hover:border-indigo-500/40 hover:bg-white/[0.03] hover:shadow-[0_10px_40px_-20px_rgba(99,102,241,0.55)]"
              >
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center text-indigo-300 group-hover:bg-indigo-500/15 group-hover:border-indigo-500/40 transition-all">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-6 font-display font-medium text-white text-xl tracking-tight">
                  {f.name}
                </h3>
                <p className="mt-2 font-body text-[14.5px] leading-relaxed text-zinc-400">
                  {f.summary}
                </p>
                <div className="mt-5 rounded-md border border-white/[0.06] bg-[#050505] px-3 py-2">
                  <span className="font-mono text-[11px] text-zinc-500">intent&nbsp;›</span>{" "}
                  <span className="font-body text-[13px] text-zinc-300">{f.example}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Solution;
